import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Clock, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { btnPrimary } from "@/components/Brand";
import { logError } from "@/lib/log";

const pad = (n) => String(n).padStart(2, "0");

const useCountdown = (seconds) => {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    setLeft(seconds);
    if (!seconds) return;
    const id = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [seconds]);
  return left;
};

/** Live trial countdown shown once per session, with the 24-hour annual offer. */
export const TrialCountdown = ({ trial }) => {
  const { t } = useI18n();
  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!trial?.is_trial) return;
    if (sessionStorage.getItem("seleksa_trial_modal")) return;
    (async () => {
      try {
        const res = await api.get("/billing/offer");
        setOffer(res.data);
      } catch (err) {
        logError("Could not load offer", err);
      }
      sessionStorage.setItem("seleksa_trial_modal", "1");
      setOpen(true);
    })();
  }, [trial]);

  const offerLeft = useCountdown(offer?.active ? offer.seconds_left : 0);
  const trialLeft = useCountdown(
    trial?.ends_at ? Math.max(0, Math.floor((new Date(trial.ends_at) - new Date()) / 1000)) : 0
  );

  if (!open || !trial?.is_trial) return null;

  const fmt = (s) => `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center px-5"
      data-testid="trial-countdown-modal">
      <div className="relative w-full max-w-md border border-[#2A2A2A] bg-[#0B0B0B] p-8 fade-up">
        <button data-testid="trial-modal-close" onClick={() => setOpen(false)}
          aria-label={t("close")} className="absolute top-4 right-4 text-[#707070] hover:text-[#D4AF37]">
          <X size={16} />
        </button>
        <p className="eyebrow flex items-center gap-2"><Clock size={12} /> {t("trial_countdown_eyebrow")}</p>
        <h2 className="mt-3 text-2xl tracking-tight">{t("trial_countdown_title")}</h2>
        <p className="mt-3 font-mono-accent text-3xl text-[#D4AF37]" data-testid="trial-days-left">
          {Math.floor(trialLeft / 86400)} {t("days")} · {fmt(trialLeft % 86400)}
        </p>
        <p className="mt-4 text-sm text-[#A8A8A8] leading-relaxed">{t("trial_countdown_body")}</p>

        {offer?.active && (
          <div className="mt-7 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5" data-testid="oto-block">
            <p className="eyebrow flex items-center gap-2 text-[#D4AF37]"><Gift size={12} /> {t("oto_eyebrow")}</p>
            <p className="mt-2 text-sm text-[#F3F3F1] leading-relaxed">
              {t("oto_body", { months: offer.bonus_months })}
            </p>
            <p className="mt-3 font-mono-accent text-xl text-[#D4AF37]" data-testid="oto-countdown">
              {fmt(offerLeft)}
            </p>
          </div>
        )}

        <div className="mt-7 flex flex-wrap gap-3">
          <Link data-testid="trial-modal-billing-link" to="/billing" className={btnPrimary} onClick={() => setOpen(false)}>
            {t("see_plans")}
          </Link>
          <button data-testid="trial-modal-later" onClick={() => setOpen(false)}
            className="text-[10px] tracking-[0.18em] uppercase text-[#707070] hover:text-[#F3F3F1] px-4">
            {t("later")}
          </button>
        </div>
      </div>
    </div>
  );
};
